import { useMutation } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import { getIntl } from '../../utils/intl';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('useOrganizationClientInvitationFile', {
    errorLoadingClientInvitationFile: 'Failed to load client invitation file',
});

export type DownloadInvitationFileArgs = {
    organizationClientInvitationId: string;
    fileId: string;
};

export type UseOrganizationClientInvitationFileArgs = {
    onLoadClientInvitationFileSuccess: (url: string) => void;
};

export type UseOrganizationClientInvitationFileReturnValue = {
    onLoadClientInvitationFile: (args: DownloadInvitationFileArgs) => void;
    isLoadingClientInvitationFile: boolean;
};

export const useOrganizationClientInvitationFile = ({
    onLoadClientInvitationFileSuccess,
}: UseOrganizationClientInvitationFileArgs): UseOrganizationClientInvitationFileReturnValue => {
    const organizationId = useOrganizationId();
    const notify = useNotify();

    const downloadInvitationFile = useMutation({
        mutationFn: async (args: DownloadInvitationFileArgs) => {
            const response =
                await appAPI.get<APITypes.organizationService.GetOrganizationClientInvitationFileDownloadUrlResponse>(
                    urls.api.organizations.clientInvitations.organizationClientInvitationFile(
                        organizationId,
                        args.organizationClientInvitationId,
                        args.fileId,
                    ),
                );
            return response.downloadUrl;
        },
        onSuccess: (data) => {
            onLoadClientInvitationFileSuccess?.(data);
        },
        onError: () => {
            notify('error', intl('errorLoadingClientInvitationFile'));
        },
    });

    return {
        onLoadClientInvitationFile: downloadInvitationFile.mutate,
        isLoadingClientInvitationFile: downloadInvitationFile.isPending,
    };
};
